import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, DollarSign, Activity, Clock, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

export default function Analytics() {
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      
      // Fetch audit logs
      const logsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-logs`, {
        headers: { }
      });
      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.logs || []);
      }

      // Fetch analytics data
      const analyticsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`, {
        headers: { }
      });
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics & Logs</h1>
          <p className="text-gray-600 mt-1">Platform insights and admin activity</p>
        </div>
        <Badge className="bg-purple-100 text-purple-800 px-4 py-2">
          <BarChart3 className="w-4 h-4 mr-2 inline" />
          Super Admin Only
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-blue-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{analytics?.total_users || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Total Users</p>
            <p className="text-xs text-green-600 mt-1">+12.5% this month</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">${analytics?.total_revenue?.toLocaleString() || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Total Revenue</p>
            <p className="text-xs text-green-600 mt-1">+18.2% this month</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-purple-600" />
              <Badge className="bg-green-100 text-green-800">Live</Badge>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analytics?.active_sessions || 0}</p>
            <p className="text-sm text-gray-600 mt-1">Active Sessions</p>
            <p className="text-xs text-gray-600 mt-1">Current users online</p>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-orange-600" />
              <Badge className="bg-blue-100 text-blue-800">Good</Badge>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analytics?.avg_response_time || 0}ms</p>
            <p className="text-sm text-gray-600 mt-1">Avg Response Time</p>
            <p className="text-xs text-gray-600 mt-1">API performance</p>
          </CardContent>
        </Card>
      </div>

      {/* User Growth Chart Placeholder */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth Trends</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Chart visualization coming soon</p>
              <p className="text-xs text-gray-400 mt-1">Integrate with charting library</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Activity Logs */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Admin Activity</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Timestamp</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Admin</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Action</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Entity</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      No activity logs yet
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-gray-900">{log.admin_name}</p>
                          <p className="text-xs text-gray-500 capitalize">{log.admin_role?.replace('_', ' ')}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="capitalize">
                          {log.action_type?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-900 capitalize">{log.entity_type}</p>
                        <p className="text-xs text-gray-500">{log.entity_id?.slice(-8)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Button size="sm" variant="outline" className="text-blue-600">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Showing {logs.length} recent activities
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
